"use client";

import Image from "next/image";
import Link from "next/link";
import type { FC } from "react";
import { ViewTransition } from "react";
import { cn } from "@/lib/utils";
import ImagePlaceholder from "./image-placeholder";

interface ProductCardProps extends React.HTMLAttributes<HTMLDivElement> {
  readonly name: string;
  readonly description: string;
  readonly code: string;
  readonly category: string;
}

export const ProductCard: FC<ProductCardProps> = ({
  name,
  description,
  code,
  category,
  ...props
}) => {
  return (
    <Link href={`/categories/${category}/${code}`} className="block h-full group">
      <div
        className={cn(
          "w-full h-full",
          "bg-card p-2 flex gap-4 rounded-xl items-start text-foreground",
          "cursor-pointer",
          "transition-all duration-300 ease-out",
          "hover:shadow-lg hover:shadow-primary/5",
          "hover:-translate-y-0.5",
        )}
        {...props}
      >
        <div className="w-1/3 shrink-0">
          <div style={{ viewTransitionName: `product-image-${code}` }}>
            {code ? (
              <div className="relative aspect-square overflow-hidden bg-white rounded-lg">
                <Image
                  unoptimized
                  src={`/images/from_catalog/${code}.png`}
                  alt={name}
                  fill
                  className="object-contain transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            ) : (
              <div className="relative aspect-square overflow-hidden flex justify-center items-center bg-accent rounded-lg">
                <ImagePlaceholder />
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 pt-1 flex-1">
          <div className="flex flex-col gap-2">
            <ViewTransition name={`product-name-${code}`}>
              <h3 className="font-medium text-lg leading-tight text-primary group-hover:text-primary/80 transition-colors duration-300">
                {name}
              </h3>
            </ViewTransition>
            <p className="text-sm leading-snug text-muted-foreground line-clamp-2">
              {description}
            </p>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
